/**
 * @file PropertiesContacts.js
 * @description Component for handling file uploads and displaying contacts in a table format.
 *
 * @component
 * @param {Object} props - The properties object.
 * @param {Object} props.item - The item object containing the current state.
 * @param {Function} props.setItem - The function to update the item state.
 *
 * @returns {JSX.Element} The rendered component.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import debounce from 'lodash/debounce';
import { FileDrop } from '../helpers/FileDrop';
import { read, utils } from 'xlsx-js-style';
import ContactsTable from '../contacts/Contacts';
import { InputField, SwitchField } from '../helpers/FormComponents';
import Tooltip from '../ui/tooltips/Tooltip';
import TagInput from '../helpers/TagInput';
import Toast from '../ui/toasts/Toast';
import { uploadFile } from '../../services/containerService';
import { useUploadContext } from '../../store/Contexts';
import { formatFileSize } from '../helpers/FileUtils';
import {
    createAsset,
    fetchAssets,
    fetchAssetByID,
} from '../../services/clientService';
import styles from '../assets/AssetLibrary.module.scss';
import AssetGridView from '../assets/components/AssetGridView';
import NotFoundBox from '../ui/no_data_indicators/NotFoundBox';
import PaginationComponent from '../helpers/PaginationComponent';
import { isValidFileTypeAndSize } from '../../utils/validate';
import { getText } from '../../i18n';

const PropertiesContacts = ({
    item,
    setItem,
    handleUploadFromDevice,
    handleAddFromAssets,
    setIsSaving,
}) => {
    const [file, setFile] = useState(null);
    const { enqueueUpload } = useUploadContext();
    const [assetName, setAssetName] = useState('');
    const [contactsTableData, setContactsTableData] = useState();
    const [isUploadFromDeviceChecked, setIsUploadFromDeviceChecked] =
        useState('');
    const [isAddFromAssetsChecked, setIsAddFromAssetsChecked] = useState('');
    const [asset, setAsset] = useState({});
    const [spreadsheetAsset, setSpreadsheetAsset] = useState({});
    const [activeCard, setActiveCard] = useState(null);
    const [assets, setAssets] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);

    const latestRequestIdRef = useRef(0);

    const handleFileChange = fileValue => {
        setFile(fileValue);
    };

    const handleSearch = event => {
        const value = event.target.value;
        setSearchTerm(value);

        debouncedLoadRef.current(value);

        if (value === '') {
            debouncedLoadRef.current.cancel();
            setCurrentPage(1);
            loadAssets(1, '');
        }
    };

    const handleSearchKeyDown = event => {
        if (event.key === 'Enter') {
            const debounced = debouncedLoadRef.current;
            debounced.cancel();
            setCurrentPage(1);
            loadAssets(1, searchTerm);
        }
    };

    const handleClick = index => {
        setActiveCard(activeCard === index ? null : index);
    };

    function updateAsset(assetObj, key, value) {
        return { ...assetObj, [key]: value };
    }

    useEffect(() => {
        handleUploadFromDevice(isUploadFromDeviceChecked);
    }, [isUploadFromDeviceChecked]);

    const loadAssets = useCallback(
        async (page = currentPage, search = searchTerm) => {
            latestRequestIdRef.current += 1;
            const requestId = latestRequestIdRef.current;

            setIsSaving(true);

            try {
                let queryParams = {
                    assetTypes: [1, 2],
                    page: page,
                    pageSize: 12,
                    fileTypes: ['xlsx', 'csv', 'numbers'],
                };
                if (search && search.trim() !== '') {
                    queryParams = { ...queryParams, search: search.trim() };
                }

                const fetchedAssets = await fetchAssets(
                    queryParams,
                    null,
                    false,
                );

                if (requestId === latestRequestIdRef.current) {
                    if (!fetchedAssets) {
                        setAssets([]);
                        setTotalPages(1);
                    } else {
                        setAssets(fetchedAssets.viewModels || []);
                        setTotalPages(fetchedAssets.pageTotal || 1);
                    }
                }
            } catch (error) {
                console.error('Failed to fetch assets', error);
                Toast.error(getText('msg_cade149a85b2'));
            } finally {
                if (latestRequestIdRef.current === requestId) {
                    setIsSaving(false);
                }
            }
        },
        [currentPage, searchTerm],
    );

    const debouncedLoadRef = useRef(
        debounce(term => {
            setCurrentPage(1);
            loadAssets(1, term);
        }, 400),
    );

    useEffect(() => {
        const debounced = debouncedLoadRef.current;
        return () => {
            debounced.cancel();
        };
    }, []);

    useEffect(() => {
        if (isAddFromAssetsChecked) {
            loadAssets(currentPage, searchTerm);
        }
        handleAddFromAssets(isAddFromAssetsChecked);
    }, [isAddFromAssetsChecked]);

    useEffect(() => {
        if (isAddFromAssetsChecked) {
            loadAssets(currentPage, searchTerm);
        }
    }, [currentPage]);

    const validateForm = () => {
        if (!asset.name || asset.name.trim() === '') {
            Toast.error(getText('msg_d7659e2c4af2'));
            return false;
        }

        if (!file) {
            Toast.error(getText('msg_e11c5988bd25'));
            return false;
        }

        const fileTypeAndSizeValidation = isValidFileTypeAndSize(file);
        if (fileTypeAndSizeValidation) {
            Toast.error(fileTypeAndSizeValidation);
            return false;
        }

        if (asset.name.length > 256) {
            Toast.error(getText('msg_f4231db74e51'));
            return false;
        }

        return true;
    };

    const uploadAsset = async () => {
        setIsSaving(true);

        if (!validateForm()) {
            setIsSaving(false);
            return;
        }

        let urlResponse;
        let isValidUrl = false;

        if (!asset.url) {
            try {
                const response = await enqueueUpload(file.name, uploadOptions =>
                    uploadFile(file, uploadOptions),
                );
                if (response.error) {
                    Toast.error(response.message);
                } else {
                    urlResponse = encodeURI(response);
                }
                try {
                    new URL(urlResponse);
                    isValidUrl = true;
                    setAsset(prev => ({
                        ...prev,
                        url: urlResponse,
                    }));
                } catch (error) {
                    Toast.error(getText('msg_3e298875de25'));
                }
            } catch (error) {
                Toast.error(getText('msg_3e298875de25'));
            }
        }

        if (isValidUrl || asset.url) {
            const newAsset = { ...asset };
            if (!newAsset.assetTypeId) newAsset.assetTypeId = 4; //ContentInProgress
            if (urlResponse) newAsset.url = urlResponse;
            newAsset.fileType = file.name.split('.').pop();
            newAsset.fileSize = formatFileSize(file.size);

            const createResponse = await createAsset(newAsset, null);
            if (createResponse.error) {
                Toast.error(createResponse.message);
            } else if (
                createResponse.status === 200 &&
                createResponse.data.url
            ) {
                Toast.success(getText('msg_9309271e92fe'));
                setSpreadsheetAsset({
                    assetId: createResponse.data.id,
                    assetTypeId: createResponse.data.assetTypeId,
                    url: createResponse.data.url,
                });
                setIsUploadFromDeviceChecked('');
                setAsset({});
                setAssetName(createResponse.data.name);
                setFile(null);
            } else {
                Toast.error(getText('msg_9f8fd8f97fd3'));
            }
        }

        setIsSaving(false);
    };

    const addAsset = () => {
        if (activeCard === null) {
            return Toast.error(
                getText('msg_c9d0d3fc9172'),
                null,
                getText('msg_40f0bdba0938'),
            );
        }

        const assetAdding = assets[activeCard];
        setSpreadsheetAsset({ assetId: assetAdding.id, url: assetAdding.url });
        setAssetName(assetAdding.name);
        setIsAddFromAssetsChecked('');
        setSearchTerm('');
        setAssets([]);
        setTotalPages(1);
        setCurrentPage(1);
        setActiveCard(null);
    };

    const readContacts = async url => {
        if (url) {
            const response = await fetch(url);
            const data = await response.arrayBuffer();
            const workbook = read(data, { type: 'array' });
            const worksheet = workbook.Sheets[workbook.SheetNames[0]];
            let contactsTableData = utils.sheet_to_json(worksheet, {
                header: 1,
            });
            setContactsTableData(contactsTableData);
        }
    };

    useEffect(() => {
        setItem(prevState => {
            const { contactsPart } = prevState;

            return {
                ...prevState,
                contactsPart: {
                    ...contactsPart,
                    data: '',
                },
                containerAssets: [spreadsheetAsset],
            };
        });

        if (spreadsheetAsset.url) {
            readContacts(spreadsheetAsset.url);
        }
    }, [spreadsheetAsset]);

    useEffect(() => {
        const fetchAssetsInit = async () => {
            if (item !== undefined) {
                if (
                    item.hasOwnProperty('containerAssets') &&
                    item.containerAssets !== null
                ) {
                    item.containerAssets = item.containerAssets.filter(
                        asset => Object.keys(asset).length > 0,
                    );
                    let tempSpreadsheetAsset = {};
                    let tempAssetName = '';

                    for (const containerAsset of item.containerAssets) {
                        if (containerAsset.assetId) {
                            const fetchedAsset = await fetchAssetByID(
                                containerAsset.assetId,
                                null,
                            );
                            containerAsset.url = fetchedAsset.url;
                            tempSpreadsheetAsset = {
                                id: containerAsset.id,
                                assetId: containerAsset.assetId,
                                url: containerAsset.url,
                            };
                            tempAssetName = fetchedAsset.name;
                        }
                    }

                    setSpreadsheetAsset(tempSpreadsheetAsset);
                    setAssetName(tempAssetName);
                }
            }
        };

        fetchAssetsInit();

        return () => {
            // Cleanup logic if necessary
        };
    }, []);

    return (
        <>
            <div
                style={{
                    width: '100%',
                    minWidth: '100%',
                    margin: 'auto',
                    paddingTop: '26px',
                    display:
                        isUploadFromDeviceChecked || isAddFromAssetsChecked
                            ? 'none'
                            : 'block',
                }}
            >
                <div
                    className="d-flex justify-content-center mb-4"
                    style={{ flexDirection: 'column', gap: '24px' }}
                >
                    <button
                        style={{ width: '100%' }}
                        className={`button-base ing-button-primary-fit-content`}
                        onClick={() => {
                            setIsAddFromAssetsChecked('spreadsheet');
                            setIsUploadFromDeviceChecked('');
                        }}
                    >
                        {getText('msg_27eb0a10db5c')}
                    </button>
                    <button
                        style={{ width: '100%' }}
                        className={`button-base button-secondary`}
                        onClick={() => {
                            setIsUploadFromDeviceChecked('spreadsheet');
                            setIsAddFromAssetsChecked('');
                        }}
                    >
                        {getText('msg_c002fe6df29c')}
                    </button>
                </div>
            </div>
            {assetName && (
                <div className="form-file-drop">
                    <div className="form-file-dropped">
                        <div className="form-file-dropped-status">
                            {getText('msg_b039895c3ee1')}
                        </div>
                        <div className="form-file-dropped-filename">
                            {assetName ?? ''}
                        </div>
                    </div>
                </div>
            )}

            {/* Upload From Device */}
            {isUploadFromDeviceChecked && (
                <>
                    <div style={{ width: '100%', paddingTop: '6px' }}>
                        <FileDrop file={file} onChange={handleFileChange} />
                    </div>

                    <InputField
                        label={getText('msg_709a23220f2c')}
                        onChange={value =>
                            setAsset(updateAsset(asset, 'name', value))
                        }
                        maxChars={256}
                        required={true}
                        placeholder={getText('msg_b7ffb9789f09')}
                        value={asset.name}
                    />

                    <div style={{ marginBottom: '24px' }}>
                        <div className="file-form-label">
                            {getText('msg_848eed0fbd54')}
                            <Tooltip
                                html="Use relevant and specific keywords to help make searching for an asset easier."
                                placement="right"
                            >
                                <i
                                    className="bi bi-info-circle"
                                    style={{
                                        paddingLeft: '5px',
                                        fontSize: '13px',
                                        color: 'var(--iNGAGED-Primary-Color)',
                                    }}
                                ></i>
                            </Tooltip>
                        </div>
                        <TagInput
                            onTagsChange={tags =>
                                setAsset(updateAsset(asset, 'assetTags', tags))
                            }
                            savedTags={asset.tags}
                        />
                    </div>

                    <div style={{ marginBottom: '24px' }}>
                        <SwitchField
                            label={getText('msg_0c359a10ae17')}
                            onChange={isChecked =>
                                setAsset(
                                    updateAsset(
                                        asset,
                                        'assetTypeId',
                                        isChecked ? 4 : 5,
                                    ),
                                )
                            }
                            tooltip={getText('msg_77453f9dd036')}
                            checked={asset.assetTypeId !== 5} //UnassignedInProgress
                        />
                    </div>

                    <div
                        id="footer"
                        style={{
                            height: 'fit-content',
                            width: '100%',
                            padding: '28px',
                            display: isUploadFromDeviceChecked
                                ? 'block'
                                : 'none',
                        }}
                    >
                        <div
                            type="button"
                            style={{
                                height: '40px',
                                width: '128px',
                                textAlign: 'center',
                                display: 'inline-block',
                            }}
                            className="float-start button-base button-secondary"
                            onClick={() => setIsUploadFromDeviceChecked('')}
                        >
                            {getText('msg_77dfd2135f4d')}
                        </div>
                        <div
                            type="button"
                            style={{
                                height: '40px',
                                width: '128px',
                                textAlign: 'center',
                                display: 'inline-block',
                            }}
                            className="float-end button-base ing-button-primary-left"
                            onClick={() => uploadAsset()}
                        >
                            {getText('msg_61cc55aa0453')}
                        </div>
                    </div>
                </>
            )}

            {/* Add From Assets */}
            {isAddFromAssetsChecked && (
                <>
                    <div
                        className={styles.searchWrapper}
                        style={{ marginBottom: '26px' }}
                    >
                        <input
                            type="text"
                            className={styles.searchInput}
                            style={{ width: '100%' }}
                            placeholder={getText('msg_b18cdf8f03e9')}
                            value={searchTerm}
                            onChange={handleSearch}
                            onKeyDown={handleSearchKeyDown}
                        />
                        <span className={styles.searchIcon}>
                            <i className="bi bi-search"></i>
                        </span>
                    </div>

                    <AssetGridView
                        assets={assets}
                        propertiesModel={true}
                        handleClick={handleClick}
                        handleDoubleClick={() => null}
                        activeCard={activeCard}
                        fullAssetName={false}
                    />

                    {assets.length < 1 && (
                        <NotFoundBox
                            backgroundColor="#f0f4ff"
                            title={
                                searchTerm
                                    ? 'Sorry, no results found'
                                    : 'Sorry, there are no assets to display'
                            }
                            description={
                                searchTerm
                                    ? 'Try adjusting your search to find what you are looking for'
                                    : `You can upload a New Asset by clicking "Cancel" on the bottom left corner, then clicking on "Upload from Device"`
                            }
                        />
                    )}

                    <PaginationComponent
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                    />

                    <div
                        id="footer"
                        style={{
                            height: 'fit-content',
                            width: '100%',
                            padding: '28px',
                            display: isAddFromAssetsChecked ? 'block' : 'none',
                        }}
                    >
                        <div
                            type="button"
                            style={{
                                height: '40px',
                                width: '128px',
                                textAlign: 'center',
                                display: 'inline-block',
                            }}
                            className="float-start button-base button-secondary"
                            onClick={() => setIsAddFromAssetsChecked('')}
                        >
                            {getText('msg_77dfd2135f4d')}
                        </div>
                        <div
                            type="button"
                            style={{
                                height: '40px',
                                width: '128px',
                                textAlign: 'center',
                                display: 'inline-block',
                            }}
                            className="float-end button-base ing-button-primary-left"
                            onClick={() => addAsset()}
                        >
                            {getText('msg_61cc55aa0453')}
                        </div>
                    </div>
                </>
            )}

            {/* If spreadsheet is loaded show contacts table */}
            {contactsTableData && (
                <div style={{ marginTop: 20 }}>
                    <ContactsTable data={contactsTableData} />
                </div>
            )}
        </>
    );
};

export default PropertiesContacts;
